import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Folder, MoreHorizontal, RotateCcw, Trash2, Archive, Globe2 } from "lucide-react";

const COLORS = {
  sand: "#D9C7A3", terracotta: "#C97B54", coral: "#E0917D", muted_rose: "#D9A0A6",
  soft_lavender: "#C9C2E0", dusty_purple: "#A98FB5", mist_blue: "#A9C0D4", muted_teal: "#7FA6A0",
  sage: "#A8B89C", olive: "#8B8C5A", warm_gray: "#A9A29A", soft_charcoal: "#6E6B66",
};

/** Mix a hex colour toward white by `w` (0 = original, 1 = white). */
export function tint(hex, w) {
  const h = (hex || "#000").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const m = (c) => Math.round(c + (255 - c) * w);
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
}

export function relativeDate(iso) {
  if (!iso) return "just now";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 7) return `${d} days ago`;
  if (d < 14) return "1 week ago";
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`;
  return `${Math.floor(d / 30)} months ago`;
}

/** Small pro card that visually tucks into the folder. */
export function TuckedProCard({ pro, rotate = 0, className = "" }) {
  return (
    <div
      className={`bg-white rounded-lg shadow-[0_3px_10px_rgba(0,0,0,0.13)] border border-black/[0.04] pl-1.5 pr-3 py-1.5 flex items-center gap-2 ${className}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <div className="w-8 h-8 rounded-full overflow-hidden bg-[#2E6B57] grid place-items-center text-[11px] font-bold text-white shrink-0">
        {pro.avatar_url ? <img src={pro.avatar_url} alt="" className="w-full h-full object-cover" /> : (pro.business_name?.[0] || "?")}
      </div>
      <div className="min-w-0">
        <p className="text-[11.5px] font-semibold leading-tight truncate max-w-[104px] text-[#2D2926]" style={{ fontFamily: "Outfit" }}>
          {pro.business_name}
        </p>
        <p className="text-[10px] text-[#2D2926]/45 leading-tight truncate max-w-[104px]">{pro.category}</p>
      </div>
    </div>
  );
}

export default function ProjectFolderCard({ project, onOpen, onChanged }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);
  const base = COLORS[project.color_id] || COLORS.sand;
  const back = tint(base, 0.42);
  const front = tint(base, 0.62);
  const isClosed = project.status === "closed";
  const previews = (project.pro_previews || []).slice(0, 2);

  const closeMenu = () => setMenuOpen(false);

  const reopen = async (e) => {
    e.stopPropagation();
    try { await api.post(`/projects/${project.id}/reopen`); toast.success("Moved back to Active Projects"); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail?.message || "Could not reopen"); }
    closeMenu();
  };

  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}"? This can't be undone. Pinned pros stay on My Board.`)) { closeMenu(); return; }
    try { await api.delete(`/projects/${project.id}`); toast.success("Project deleted"); onChanged(); }
    catch { toast.error("Could not delete"); }
    closeMenu();
  };

  return (
    <div className="relative group" data-testid={`folder-${project.id}`}>
      <button
        onClick={() => onOpen(project)}
        className="block w-full text-left relative h-[212px] focus:outline-none"
        data-testid={`folder-open-${project.id}`}
        aria-label={`Open project ${project.name}`}
      >
        {/* raised folder tab */}
        <div
          className="absolute top-0 left-4 w-[150px] h-6 rounded-t-[10px]"
          style={{ background: back, boxShadow: "0 -1px 3px rgba(0,0,0,0.07)" }}
        />
        {/* folder back panel */}
        <div
          className="absolute left-0 right-0 top-5 bottom-[6px] rounded-tr-[14px] rounded-b-[14px] rounded-tl-[4px]"
          style={{ background: back, boxShadow: "0 2px 6px rgba(0,0,0,0.07)" }}
        />

        {/* pro cards tucked inside */}
        <div className="absolute left-6 right-6 top-8 flex items-start gap-2.5">
          {previews.length > 0 ? previews.map((p, i) => (
            <TuckedProCard key={p.user_id} pro={p} rotate={i === 0 ? -1.4 : 1.2} />
          )) : (
            <span className="text-[11px] text-[#2D2926]/45 italic bg-white/70 rounded-lg px-3 py-2 shadow-sm">
              No professionals yet — tap to add
            </span>
          )}
          {project.pro_count > 2 && (
            <span className="mt-2 text-[11px] font-semibold text-[#2D2926]/50">+{project.pro_count - 2}</span>
          )}
        </div>

        {/* folder front flap */}
        <div
          className="absolute left-0 right-0 bottom-0 h-[112px] rounded-b-[16px] rounded-t-[10px] px-6 pt-4 transition-transform duration-300 group-hover:-translate-y-0.5"
          style={{
            background: `linear-gradient(180deg, ${front} 0%, ${tint(base, 0.52)} 100%)`,
            boxShadow: "0 -2px 6px rgba(0,0,0,0.06), 0 10px 22px rgba(0,0,0,0.10)",
            borderTop: "1px solid rgba(255,255,255,0.55)",
          }}
        >
          <p className="text-[19px] font-bold text-[#2D2926] truncate pr-10" style={{ fontFamily: "Outfit" }}>
            {project.name}
          </p>
          <p className="text-[13px] text-[#2D2926]/65 mt-1">
            {project.pro_count} professional{project.pro_count === 1 ? "" : "s"}
          </p>
          <p className="text-[13px] text-[#2D2926]/55">
            Last updated {relativeDate(project.updated_at)}
          </p>
          {isClosed && (
            <span className="absolute left-6 bottom-3 text-[10px] uppercase tracking-wider font-bold text-[#2D2926]/40 flex items-center gap-1">
              <Archive size={10} /> Filed
            </span>
          )}
        </div>
      </button>

      <div className="absolute bottom-[70px] right-5 z-20" ref={ref}>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
          className="w-8 h-8 rounded-full bg-white/70 hover:bg-white grid place-items-center shadow-sm transition"
          data-testid={`folder-menu-${project.id}`}
          aria-label="Project options"
        >
          <MoreHorizontal size={15} className="text-[#2D2926]/70" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-9 z-30 bg-white rounded-xl shadow-xl border border-black/10 py-1 w-52 text-sm" onClick={(e) => e.stopPropagation()} data-testid={`folder-menu-open-${project.id}`}>
            <button onClick={() => { onOpen(project); closeMenu(); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2"><Folder size={13} /> Open</button>
            {!isClosed ? (
              <button onClick={() => { onOpen(project, "close"); closeMenu(); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2" data-testid={`folder-move-to-cabinet-${project.id}`}><Archive size={13} /> Move to Filing Cabinet</button>
            ) : (
              <button onClick={reopen} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2" data-testid={`folder-reopen-${project.id}`}><RotateCcw size={13} /> Reopen project</button>
            )}
            <button onClick={() => { onOpen(project, "sharing"); closeMenu(); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2"><Archive size={13} className="rotate-90" /> Sharing</button>
            <button onClick={() => { onOpen(project, "publish"); closeMenu(); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2"><Globe2 size={13} /> Show on profile</button>
            <button onClick={del} className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 size={13} /> Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}

export { COLORS as PROJECT_FOLDER_COLORS };
